import { useEffect, useState, useSyncExternalStore } from 'react';

import { apiFetch } from '@/lib/http/client';
import type { CalendarEvent } from '@/stores/use-calendar-store';

// ── Module cache + invalidation pub/sub ────────────────────────────────────────
//
// Mirrors use-entity-attendees.ts: one fetch per (entityId, surfaceId) scope,
// shared by every mounted consumer.

const cache = new Map<string, Promise<CalendarEvent | null>>();
const versions = new Map<string, number>();
const listeners = new Set<() => void>();

function scopeKey(entityId: string, surfaceId: string): string {
  return `${entityId}::${surfaceId}`;
}

function eventUrl(entityId: string, surfaceId: string): string {
  return `/workspace/integrations/calendar/events/for-surface?entityId=${encodeURIComponent(entityId)}&surfaceId=${encodeURIComponent(surfaceId)}`;
}

function versionOf(key: string): number {
  return versions.get(key) ?? 0;
}

/** Drop the cached event for a scope and notify mounted hooks to refetch. */
export function invalidateSurfaceCalendarEvent(entityId: string, surfaceId: string): void {
  const key = scopeKey(entityId, surfaceId);
  cache.delete(key);
  versions.set(key, versionOf(key) + 1);
  listeners.forEach((l) => l());
}

/**
 * Drop every surface's cached event for one entity.
 *
 * For changes whose blast radius is the document rather than one surface —
 * unlinking from the calendar side removes the document from the event on
 * every surface it was recorded into, and that caller holds no list of which
 * surfaces those were.
 */
export function invalidateEntitySurfaceCalendarEvents(entityId: string): void {
  const prefix = `${entityId}::`;
  let dropped = false;
  for (const key of [...cache.keys()]) {
    if (!key.startsWith(prefix)) continue;
    cache.delete(key);
    versions.set(key, versionOf(key) + 1);
    dropped = true;
  }
  if (dropped) listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function versionFor(entityId: string | null, surfaceId: string | null): number {
  return entityId && surfaceId ? versionOf(scopeKey(entityId, surfaceId)) : 0;
}

/** Test-only: clear the module cache + versions between specs. */
export function resetSurfaceCalendarEventCacheForTests(): void {
  cache.clear();
  versions.clear();
}

function fetchSurfaceEvent(entityId: string, surfaceId: string): Promise<CalendarEvent | null> {
  const key = scopeKey(entityId, surfaceId);
  let promise = cache.get(key);
  if (!promise) {
    promise = apiFetch<CalendarEvent | null>(eventUrl(entityId, surfaceId)).catch((err: unknown) => {
      cache.delete(key);
      throw err;
    });
    cache.set(key, promise);
  }
  return promise;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export type UseSurfaceCalendarEventResult = {
  event: CalendarEvent | null;
  isLoading: boolean;
};

/**
 * The calendar event the active surface was recorded from, if any. Exact
 * match per surface — no document-level fallback (design §3.3). Idle when
 * `surfaceId` is null: a blank/document-level surface has no meeting of its
 * own to resolve.
 */
export function useSurfaceCalendarEvent(
  entityId: string | null,
  surfaceId: string | null,
): UseSurfaceCalendarEventResult {
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const version = useSyncExternalStore(
    subscribe,
    () => versionFor(entityId, surfaceId),
    () => 0,
  );

  useEffect(() => {
    if (!entityId || !surfaceId) {
      setEvent(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    // Drop the previous scope's event before fetching this one, or the chip
    // renders the OLD surface's meeting until the network answers — showing
    // the wrong meeting rather than nothing.
    setEvent(null);
    setIsLoading(true);
    fetchSurfaceEvent(entityId, surfaceId)
      .then((result) => {
        if (cancelled) return;
        setEvent(result);
      })
      .catch(() => {
        if (cancelled) return;
        setEvent(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `version` bumps on invalidate → re-run → cache miss → refetch.
  }, [entityId, surfaceId, version]);

  return { event, isLoading };
}
